/**
=========================================================
* Annotation Portal React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useMemo } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Annotation Portal React example components
import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";

// Annotation Portal React themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";

// Annotation Portal React Dark Mode themes
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Annotation Portal React routes
import { useTranslatedRoutes } from "routes";

// Annotation Portal React contexts
import { useMaterialUIController } from "context";

// Images
import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// custom import
import Dashboard from "layouts/dashboard";
import Basic from "layouts/authentication/sign-in";

import { getUserInfo } from "services/authService";
import MDBox from "components/MDBox";
import { LanguageProvider } from "context/LanguageContext";
import { LoadingProvider } from "context/LoadingContext";
import { SessionProvider } from "context/SessionContext";
import GlobalLoader from "components/GlobalLoader";
import SessionExpiredDialog from "components/SessionExpiredDialog";
import { useTranslation } from "hooks/useTranslation";

function AppContent() {
  const [controller] = useMaterialUIController();
  const { direction, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [rtlCache, setRtlCache] = useState(null);
  const { pathname } = useLocation();
  const { t } = useTranslation();
  const translatedRoutes = useTranslatedRoutes();

  // Cache for the rtl
  useMemo(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
  }, []);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return <Route exact path={route.route} element={route.component} key={route.key} />;
      }

      return null;
    });

  const user = getUserInfo();
  let filteredRoutes = [];
  if (user && "role" in user) {
    filteredRoutes = translatedRoutes.filter((route) => route.roles.includes(user.role));
  }

  return direction === "rtl" ? (
    <CacheProvider value={rtlCache}>
      <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName={t("navbar.title")}
              routes={filteredRoutes}
            ></Sidenav>
            <Configurator />
          </>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {getRoutes(filteredRoutes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </ThemeProvider>
    </CacheProvider>
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
            brandName={t("navbar.title")}
            routes={filteredRoutes}
          ></Sidenav>
          <Configurator />
          {/* {configsButton} */}
        </>
      )}
      {layout === "vr" && <Configurator />}
      <Routes>
        <Route path="/" element={<Navigate to="/authentication/sign-in" replace />} />
        <Route path="/authentication/sign-in" element={<Basic />} />
        <Route path="/dashboard" element={<Dashboard />} />
        {filteredRoutes.map((route) => {
          return <Route key={route.key} path={route.route} element={route.component} />;
        })}
      </Routes>
    </ThemeProvider>
  );
}

export default function App() {
  return (
    <LanguageProvider>
      <LoadingProvider>
        <SessionProvider>
          <GlobalLoader />
          <SessionExpiredDialog />
          <AppContent />
        </SessionProvider>
      </LoadingProvider>
    </LanguageProvider>
  );
}
